import * as React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const FAQ_ITEMS = [
  {
    id: "faq-costs",
    q: "What does it cost?",
    a: "Projects start with a free audit. Most pilots range from a few hundred to a few thousand dollars depending on scope. We agree on a clear fixed or milestone-based price before work begins.",
  },
  {
    id: "faq-timeline",
    q: "How long does it take?",
    a: "Quick wins can ship in 1–2 weeks. Larger rollouts typically take 3–6 weeks with weekly check-ins.",
  },
  {
    id: "faq-privacy",
    q: "How do you handle data and privacy?",
    a: "We follow least‑privilege access, encrypt data in transit, and use reputable providers. We scope automations to minimize sensitive data exposure.",
  },
  {
    id: "faq-hipaa",
    q: "Are you HIPAA compliant?",
    a: "We can design HIPAA‑conscious workflows and use vendor BAAs where applicable. Final compliance depends on your selected tools and policies.",
  },
  {
    id: "faq-integrations",
    q: "What systems can you integrate with?",
    a: "Common integrations include Google/Outlook Calendars, Clio, Dentrix, HubSpot, plus Email/SMS via trusted gateways, and Zapier/Make for custom flows.",
  },
  {
    id: "faq-results",
    q: "When will we see results?",
    a: "Most teams see time savings in the first 2–4 weeks as repetitive work gets automated and fewer no‑shows occur.",
  },
  {
    id: "faq-ownership",
    q: "Who owns the data and outputs?",
    a: "You retain ownership of your data and outputs. We’ll document how data flows and where it’s stored.",
  },
  {
    id: "faq-cancellation",
    q: "Can we pause or cancel?",
    a: "Yes. You can pause or cancel at any time. We’ll ensure a clean handoff of configurations and assets.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FAQ: React.FC = () => {
  const [value, setValue] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    const setFromHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (FAQ_ITEMS.some((f) => f.id === hash)) setValue(hash);
    };
    setFromHash();
    window.addEventListener("hashchange", setFromHash);
    return () => window.removeEventListener("hashchange", setFromHash);
  }, []);

  return (
    <section id="faq" className="py-16 md:py-24">
      <div className="container mx-auto">
        <header className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold">Frequently Asked Questions</h2>
          <p className="text-lg md:text-xl text-muted-foreground mt-2">Answers to common questions about costs, timeline, privacy, and integrations.</p>
        </header>

        <Accordion type="single" collapsible value={value} onValueChange={setValue}>
          {FAQ_ITEMS.map((f) => (
            <AccordionItem key={f.id} value={f.id} id={f.id}>
              <AccordionTrigger>
                <span className="inline-flex items-center gap-2">
                  {f.q}
                  <a href={`#${f.id}`} className="text-base text-muted-foreground hover:underline">
                    #{f.id.replace("faq-", "")}
                  </a>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-base text-muted-foreground">{f.a}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FAQ;
